// ─── Sub-channel derivation ─────────────────────────────────────────
// Sky-Q-style "5.1 / 5.2 / 5.3" sub-channels are derived on the
// frontend from the `show_title` field on each tape. Zero backend
// schema changes at the channel level — we synthesize virtual
// channel entries into the lineup at runtime so downstream code
// (guide, browse, CRT playback) treats them like real channels.
//
// A channel only spawns sub-channels when it carries ≥ 2 distinct
// show titles. Otherwise the derivation is a no-op and the channel
// renders exactly as before.
//
// Sub-channel IDs are stable across refreshes: `{parentId}::sub::{slug}`
// where the slug is a lowercased-dashed form of the show title. That
// way a viewer bookmarked on `5.1 SONIC UNDERGROUND` stays on the
// same feed even if a new show is added to the channel later (which
// would shift the raw ordering).

const SLUG_RE = /[^a-z0-9]+/g;

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(SLUG_RE, "-")
    .replace(/(^-+)|(-+$)/g, "")
    .slice(0, 60);
}

/**
 * Expand a raw channels-array + videosByChannel-map into a lineup that
 * includes derived sub-channels for any channel with ≥ 2 shows.
 *
 * Returns { channels, videosByChannel } — same shape as inputs so the
 * caller can drop them in place of the originals.
 *
 * Sub-channel entries carry `isSubChannel: true` + `parent_id` so
 * owner-only paths can filter them out (uploads, deletes, thumbnail
 * edits target real channels only).
 */
export function expandChannelsWithShows(channels, videosByChannel) {
  if (!Array.isArray(channels) || channels.length === 0) {
    return { channels: channels || [], videosByChannel: videosByChannel || {} };
  }
  const out = [];
  const outVids = { ...(videosByChannel || {}) };

  for (const ch of channels) {
    out.push(ch);
    if (ch.virtual || ch.isSubChannel) continue;

    const tapes = videosByChannel[ch.id] || [];
    if (tapes.length === 0) continue;

    // Bucket by show_title, preserving first-appearance order (Map
    // insertion order = the order the operator most likely thinks of
    // when they numbered the shows on the channel).
    const shows = new Map();
    for (const v of tapes) {
      const raw = (v.show_title || "").trim();
      if (!raw) continue;
      if (!shows.has(raw)) shows.set(raw, []);
      shows.get(raw).push(v);
    }
    // Prune singleton shows so a freshly-matched loner tape doesn't
    // become its own one-tape sub-channel. Those tapes stay under
    // the parent's playlist until at least one sibling with the same
    // show title joins them. Matches the operator's expectation that
    // "fixing an unmatched tape" enriches metadata, not the guide layout.
    for (const [k, arr] of [...shows.entries()]) {
      if (arr.length < 2) shows.delete(k);
    }
    if (shows.size < 2) continue;

    let subIdx = 0;
    for (const [showTitle, showTapes] of shows) {
      subIdx += 1;
      const slug = slugify(showTitle) || `show-${subIdx}`;
      const subId = `${ch.id}::sub::${slug}`;
      // Curator override takes precedence: `parent.show_art[showTitle]`
      // is set via `POST /api/channels/{parent}/show-art/link` (from
      // the guide's LINK URL popover when the operator is browsing a
      // sub-channel). Falls back to a first-tape backdrop cascade so
      // brand-new sub-channels still have distinctive art.
      const curatedArt = ch.show_art && typeof ch.show_art === "object" ? ch.show_art[showTitle] : null;
      const subBackdrop =
        curatedArt ||
        showTapes.find((t) => t.backdrop_url)?.backdrop_url ||
        showTapes.find((t) => t.poster_url)?.poster_url ||
        ch.thumbnail_url ||
        null;
      const parentNumber = ch.number || 0;
      out.push({
        // Spread parent so category_id / owner_id / etc. carry over,
        // then override the display-facing fields.
        ...ch,
        id: subId,
        parent_id: ch.id,
        parent_name: ch.name,
        parent_number: parentNumber,
        isSubChannel: true,
        sub_index: subIdx,
        // Keep `number` matching the parent so sort-by-number keeps
        // subs adjacent to their parent. `numberLabel` is what the UI
        // actually renders — e.g. "05.1".
        number: parentNumber,
        numberLabel: `${String(parentNumber).padStart(2, "0")}.${subIdx}`,
        name: showTitle,
        show_title: showTitle,
        description: `${showTapes.length} tape${showTapes.length === 1 ? "" : "s"} of ${showTitle}`,
        // Landscape thumbnail overrides — clear the uploaded path so
        // the URL wins and the guide tile shows this show's art. When
        // the cascade produces null (no per-show art, no tape backdrop,
        // parent has an uploaded-only thumb), stash the parent's
        // thumbnail path/url on the sub so ChannelThumb can proxy to
        // `/channels/{parent}/thumbnail` at render time as a graceful
        // last-resort fallback (better than "NO SIG").
        thumbnail_url: subBackdrop,
        thumbnail_path: null,
        _parentThumbPath: ch.thumbnail_path || null,
        _parentThumbUrl: ch.thumbnail_url || null,
        // Cross-fade sub-channels shouldn't inherit the parent's
        // guide-poster (that's the mixed-content poster). Let the
        // guide fall back to the derived thumbnail instead.
        guide_poster_url: null,
        guide_poster_path: null,
        logo_url: null,
        logo_path: null,
        // Per-sub MEGA preview overrides. Copied from parent's
        // `show_mega[showTitle]` if present; each sub can carry its
        // own URL + prefer flag so the operator can point different
        // sub-channels at different MEGA teasers.
        preview_mega_url:
          (ch.show_mega && typeof ch.show_mega === "object" && ch.show_mega[showTitle]?.url) || null,
        preview_prefer_mega:
          !!(ch.show_mega && typeof ch.show_mega === "object" && ch.show_mega[showTitle]?.prefer),
      });
      outVids[subId] = showTapes;
    }
  }

  return { channels: out, videosByChannel: outVids };
}

/**
 * True when the id was minted by `expandChannelsWithShows`. Useful for
 * owner-side guards that need to skip sub-channels.
 */
export function isSubChannelId(id) {
  return typeof id === "string" && id.includes("::sub::");
}

/**
 * Given a possibly-sub-channel id, return the real parent channel id.
 * Real ids pass through unchanged so callers can use this
 * unconditionally.
 */
export function parentChannelId(id) {
  if (!isSubChannelId(id)) return id;
  return id.split("::sub::")[0];
}
